import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useWallet } from '../contexts/ServiceContext';
import { DAOGovernanceService } from '../services/DAOGovernanceService';
import { ReputationService } from '../services/ReputationService';
import { sanitizeInput, sanitizeErrorMessage } from '../utils/security';
import './ContentFlagging.css';

const ContentFlagging = ({ contentId, contentType = 'POST', onFlagSubmitted, onClose }) => {
  const { address: walletAddress } = useWallet();
  const [flagData, setFlagData] = useState({
    category: '',
    severity: 'MEDIUM',
    description: '',
    evidence: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userReputation, setUserReputation] = useState(null);
  const [requiredStake, setRequiredStake] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const daoService = new DAOGovernanceService();
  const reputationService = new ReputationService();

  // Flag categories with descriptions
  const FLAG_CATEGORIES = {
    SPAM: {
      label: 'Spam',
      description: 'Repetitive, promotional, or irrelevant content',
      icon: '🚫',
      minStake: 10
    },
    HARASSMENT: {
      label: 'Harassment',
      description: 'Bullying, threats, or targeted harassment',
      icon: '⚠️',
      minStake: 25
    },
    HATE_SPEECH: {
      label: 'Hate Speech',
      description: 'Content promoting hatred based on identity',
      icon: '🛑',
      minStake: 50
    },
    MISINFORMATION: {
      label: 'Misinformation',
      description: 'False or misleading information',
      icon: '❌',
      minStake: 30
    },
    INAPPROPRIATE: {
      label: 'Inappropriate Content',
      description: 'NSFW or age-inappropriate material',
      icon: '🔞',
      minStake: 20
    },
    COPYRIGHT: {
      label: 'Copyright Violation',
      description: 'Unauthorized use of copyrighted material',
      icon: '©️',
      minStake: 40
    },
    OTHER: {
      label: 'Other',
      description: 'Other policy violations',
      icon: '📋',
      minStake: 15
    }
  };

  const SEVERITY_LEVELS = {
    LOW: {
      label: 'Low',
      description: 'Minor policy violation',
      color: '#28a745',
      multiplier: 1
    },
    MEDIUM: {
      label: 'Medium',
      description: 'Moderate policy violation',
      color: '#ffc107',
      multiplier: 1.5
    },
    HIGH: {
      label: 'High',
      description: 'Serious policy violation',
      color: '#dc3545',
      multiplier: 2
    }
  };

  useEffect(() => {
    if (walletAddress) {
      loadUserData();
    }
  }, [walletAddress]);

  useEffect(() => {
    calculateRequiredStake();
  }, [flagData.category, flagData.severity, userReputation]);

  const loadUserData = async () => {
    try {
      const reputation = await reputationService.getUserReputation(walletAddress);
      setUserReputation(reputation);
    } catch (error) {
      console.error('Error loading user data:', sanitizeErrorMessage(error));
    }
  };

  const calculateRequiredStake = () => {
    if (!flagData.category) {
      setRequiredStake(0);
      return;
    }

    const baseStake = FLAG_CATEGORIES[flagData.category]?.minStake || 10;
    const severityMultiplier = SEVERITY_LEVELS[flagData.severity]?.multiplier || 1;
    
    // Reputation discount (higher reputation = lower stake required)
    const reputationDiscount = userReputation ? Math.min(0.5, userReputation.score / 2000) : 0;
    
    const finalStake = Math.max(5, Math.floor(baseStake * severityMultiplier * (1 - reputationDiscount)));
    setRequiredStake(finalStake);
  };

  const handleInputChange = (field, value) => {
    const sanitizedValue = sanitizeInput(value);
    setFlagData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
    setError('');
  };

  const validateForm = () => {
    if (!flagData.category) {
      setError('Please select a flag category');
      return false;
    }

    if (!flagData.description.trim()) {
      setError('Please provide a description of the violation');
      return false;
    }

    if (flagData.description.length < 10) {
      setError('Description must be at least 10 characters long');
      return false;
    }

    if (flagData.description.length > 500) {
      setError('Description must be less than 500 characters');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Check if user can afford the stake
      const canAffordStake = await reputationService.canAffordAction(walletAddress, 'FLAG_CONTENT', requiredStake);
      if (!canAffordStake.allowed) {
        throw new Error(`Insufficient tokens to flag content. Required: ${requiredStake} ZNOOZINU`);
      }

      // Submit flag to DAO governance
      const result = await daoService.flagContent(
        contentId,
        contentType,
        {
          ...flagData,
          flagger: walletAddress,
          stake: requiredStake,
          timestamp: Date.now()
        }
      );

      if (result.success) {
        setSuccess('Content flagged successfully! A DAO proposal has been created for community review.');
        
        // Deduct stake from user's balance
        await reputationService.deductStake(walletAddress, requiredStake, 'CONTENT_FLAG');
        
        // Update user reputation for responsible flagging
        await reputationService.updateReputation(walletAddress, 'FLAG_SUBMITTED', 5);

        if (onFlagSubmitted) {
          onFlagSubmitted(result);
        }

        // Auto-close after success
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to submit flag');
      }
    } catch (error) {
      console.error('Error submitting flag:', sanitizeErrorMessage(error));
      setError(sanitizeErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!walletAddress) {
    return (
      <div className="flag-content-modal">
        <div className="flag-content-container">
          <div className="flag-header">
            <h3>Flag Content</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="flag-error">
            <p>Please connect your wallet to flag content.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flag-content-modal">
      <div className="flag-content-container">
        <div className="flag-header">
          <h3>🚩 Flag Content</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {userReputation && (
          <div className="user-info">
            <div className="reputation-badge">
              <span className="reputation-score">{userReputation.score}</span>
              <span className="reputation-label">Reputation</span>
            </div>
            <div className="tier-badge">
              <span className="tier-name">{userReputation.tier}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flag-form">
          <div className="form-section">
            <label className="form-label">Flag Category *</label>
            <div className="category-grid">
              {Object.entries(FLAG_CATEGORIES).map(([key, category]) => (
                <div
                  key={key}
                  className={`category-option ${flagData.category === key ? 'selected' : ''}`}
                  onClick={() => handleInputChange('category', key)}
                >
                  <div className="category-icon">{category.icon}</div>
                  <div className="category-info">
                    <div className="category-label">{category.label}</div>
                    <div className="category-description">{category.description}</div>
                    <div className="category-stake">{category.minStake} ZNOOZINU</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Severity Level *</label>
            <div className="severity-options">
              {Object.entries(SEVERITY_LEVELS).map(([key, severity]) => (
                <label key={key} className="severity-option">
                  <input
                    type="radio"
                    name="severity"
                    value={key}
                    checked={flagData.severity === key}
                    onChange={(e) => handleInputChange('severity', e.target.value)}
                  />
                  <div className="severity-info">
                    <div 
                      className="severity-indicator" 
                      style={{ backgroundColor: severity.color }}
                    ></div>
                    <div>
                      <div className="severity-label">{severity.label}</div>
                      <div className="severity-description">{severity.description}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Description *</label>
            <textarea
              value={flagData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe why this content violates community guidelines..."
              className="form-textarea"
              rows={4}
              maxLength={500}
            />
            <div className="char-counter">
              {flagData.description.length}/500 characters
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Evidence (Optional)</label>
            <textarea
              value={flagData.evidence}
              onChange={(e) => handleInputChange('evidence', e.target.value)}
              placeholder="Provide any additional evidence or context..."
              className="form-textarea"
              rows={3}
              maxLength={300}
            />
            <div className="char-counter">
              {flagData.evidence.length}/300 characters
            </div>
          </div>

          <div className="stake-info">
            <div className="stake-amount">
              <span className="stake-label">Required Stake:</span>
              <span className="stake-value">{requiredStake} ZNOOZINU</span>
            </div>
            <div className="stake-note">
              Your stake will be returned if the community votes to uphold your flag.
              If the flag is rejected, you may lose part of your stake.
            </div>
          </div>

          {error && (
            <div className="flag-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="flag-success">
              <span className="success-icon">✅</span>
              {success}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting || !flagData.category || !flagData.description.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting Flag...
                </>
              ) : (
                <>
                  🚩 Submit Flag ({requiredStake} ZNOOZINU)
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentFlagging;
